<template>
  <div
    class="notification-center v-notification-center"
  >
    <el-popover
      ref="notification"
      placement="bottom-start"
      popper-class="notification-center-popper"
      trigger="click"
      @show="onShowNotification"
    >
      <template #reference>
        <el-button
          class="notification-bell-button"
          :class="{ muted: doNotDisturb }"
          data-testid="notification-center-bell-button"
          type="ghost"
        >
          <span
            v-if="showSpinner"
            class="label loading-spinner"
          >
            <el-loading class="spinner-xxs" />
          </span>
          <span
            v-else-if="showCounter"
            class="label counter"
          >
            {{ notificationsCount }}
          </span>
          <el-icon
            name="bell-o"
            variant="24"
          />
        </el-button>
      </template>
      <notification-dropdown
        class="notifications-dropdown"
        :show-popper="showPopper"
      />
    </el-popover>
  </div>
</template>

<script lang="ts">
  import NotificationDropdown from '@/common/components/notification-center/NotificationDropdown.vue';
  import { NotificationType } from '@/common/constants/notifications.constants';
  import { useNotificationsStore } from '@/common/store/pinia/notifications';
  import { mapStores } from 'pinia';
  import type ElPopover from '@uikit/ui-kit/packages/popover';

  export const MAX_NOTIFICATION_COUNTER_VALUE = 9999;

  type NotificationPopoverRef = InstanceType<ElPopover> | null;

  export default {
    name: 'NotificationCenter',
    components: {
      NotificationDropdown
    },
    data() {
      return {
        notification: null as NotificationPopoverRef,
      };
    },
    beforeDestroy() {
      this.detachEvent();
    },
    mounted() {
      this.notification = this.$refs.notification as NotificationPopoverRef;
    },
    computed: {
      ...mapStores(useNotificationsStore),
      doNotDisturb() {
        return this.notificationsStore.doNotDisturb;
      },
      mainNotifications() {
        return this.notificationsStore.mainNotifications;
      },
      showPopper() {
        return !!this.notification?.showPopper;
      },
      notificationsNotSeen() {
        return this.mainNotifications.filter(it => !it.isSeen);
      },
      inProgressNotifications() {
        return this.mainNotifications.filter(it => it.type === NotificationType.PROGRESS);
      },
      notificationsCount() {
        return Math.min(MAX_NOTIFICATION_COUNTER_VALUE, this.notificationsNotSeen.length);
      },
      inProgressCount() {
        return this.inProgressNotifications.length;
      },
      preventReloadsCount() {
        return this.inProgressNotifications.filter(it => it.preventReload).length;
      },
      showCounter() {
        return !this.doNotDisturb && this.notificationsCount;
      },
      showSpinner() {
        return !this.doNotDisturb && this.inProgressCount;
      }
    },
    watch: {
      preventReloadsCount: {
        immediate: true,
        handler(newValue, prevValue) {
          if (!prevValue && newValue > 0) {
            this.addEvent();
          } else if (newValue === 0 && prevValue > 0) {
            this.detachEvent();
          }
        }
      }
    },
    methods: {
      onShowNotification() {
        this.notificationsStore.setSeenNotifications();
      },
      addEvent() {
        window.addEventListener('beforeunload', this.confirmWindow);
      },
      detachEvent() {
        window.removeEventListener('beforeunload', this.confirmWindow);
      },
      confirmWindow(e) {
        e.preventDefault();
        const message = this.$t('At least one operation is in progress');
        e.returnValue = message;
        return message;
      },
    }
  };
</script>

<style scoped lang="scss">
.notification-bell-button {
  position: relative;
  line-height: 24px;

  ::v-deep .el-button__text {
    font-size: $hci-font-size-zero;
  }

  &.muted .i {
    opacity: 0.3;
  }
}

.notifications-dropdown {
  width: 559px;
  padding: 0;

  ::v-deep .el-select-dropdown__list {
    padding: 0;
  }
}

.notifications-count {
  background: $av-fixed-danger;
  color: $av-inversed-primary;
}

.label {
  display: inline-block;
  position: absolute;
  top: 1px;
  right: -6px;
  z-index: $av-z-index-label;
  font-size: $av-font-size-xxxs;
  line-height: $av-line-height-xs;
  box-shadow: 0 0 0 2px $av-inversed-primary;
  pointer-events: none;
}

.counter {
  background: $av-fixed-danger;
  border-radius: 8px;
  padding: 0 8px;
  letter-spacing: 0.5px;
  color: $av-inversed-primary;
  font-weight: $av-font-weight-bold;
}

.loading-spinner {
  top: 0;
  right: 0;
  width: 16px;
  height: 16px;
  background: $av-brand-primary;
  border-radius: $av-border-radius-circle;
  display: flex;
  align-items: center;
  justify-content: center;

  .el-spinner.spinner-xxs {
    width: 10px;
    height: 10px;
    border-color: $av-inversed-primary;
    animation: rotate 0.8s linear infinite;
  }
}
</style>

<style lang="scss">
.notification-center-popper {
  max-height: 90%;
  margin-right: 12px;
}
</style>
